import { createContext, useContext, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { effectiveProjectRole, type Project, type ProjectRole } from '@allemnii-pulse/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { useOrgs } from '../orgs/OrgProvider.js';
import { useDocument } from '../../lib/firestore.js';

interface ProjectContextValue {
  projectId: string;
  project: Project | null;
  loading: boolean;
  /** The caller's effective role — see effectiveProjectRole: their explicit membership, raised to
   * what their org role implies (org admin → lead, org owner → owner), guests capped. Gate UI on
   * this with canProject(), never on the raw membership doc. */
  role: ProjectRole | null;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

/** Reads projects/{projectId} live (see docs/PLAN.md §2.1 — reads go straight to Firestore) and
 * the caller's own membership doc, combined with their org role into the effective project role
 * the API also enforces. Every project page renders under this. */
export function ProjectProvider({ children }: { children: ReactNode }) {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const { orgs } = useOrgs();
  const { data: project, loading } = useDocument<Project>(
    projectId ? `projects/${projectId}` : null,
  );
  const { data: membership } = useDocument<{ role: ProjectRole }>(
    projectId && user ? `projects/${projectId}/members/${user.uid}` : null,
  );

  if (!projectId) throw new Error('ProjectProvider rendered outside a :projectId route');
  const orgRole = project ? orgs.find((o) => o.id === project.orgId)?.role : undefined;
  const role = effectiveProjectRole(orgRole, membership?.role);

  return (
    <ProjectContext.Provider value={{ projectId, project, loading, role }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used within ProjectProvider');
  return ctx;
}
